import { useState } from 'react';
import { CertificationInterestForm } from '../components/forms/CertificationInterestForm';
import { SafeLabsCTA } from '../components/SafeLabsCTA';

const LabsPage = () => {
  const [showCertForm, setShowCertForm] = useState(false);

  return (
    <div data-testid="labs-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              SAFE Labs
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              SAFE Labs is where safety is proven.
            </p>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              AI systems are not safe by design, policy, or intent.
            </p>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              They are safe only if they pass independent verification.
            </p>
            <p className="text-lg md:text-xl text-white font-semibold">
              SAFE Labs determines that outcome.
            </p>
          </div>
        </div>
      </section>

      {/* Pass / Fail */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Pass / Fail</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">SAFE Labs does not assess safety.</p>
              <p className="text-lg text-slate-700">It determines whether a system meets the SAFE standard.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-xl mt-8">
            <div className="bg-slate-900 text-white p-8 text-center">
              <p className="text-3xl font-bold">Pass</p>
            </div>
            <div className="bg-slate-200 text-slate-900 p-8 text-center">
              <p className="text-3xl font-bold">Fail</p>
            </div>
          </div>
          <p className="text-lg text-slate-900 font-semibold mt-8 max-w-3xl">
            There is no partial certification.
          </p>
        </div>
      </section>

      {/* Industry Reality */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Industry Reality</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">Most AI systems are tested internally.</p>
              <p className="text-lg text-slate-700">Many are evaluated.</p>
              <p className="text-lg text-slate-700">Few are independently verified.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Internal testing is not proof.
            </p>
          </div>
        </div>
      </section>

      {/* Testing Model */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">Testing Model</p>
            <p className="text-lg text-slate-700 mb-6">
              SAFE Labs conducts independent verification through:
            </p>
            <div className="space-y-3 mb-10">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Adversarial scenario testing</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Behavioral risk simulation</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Real-world failure mode analysis</p>
              </div>
            </div>
            <p className="text-lg text-slate-700 mb-6">
              Systems are tested under conditions of:
            </p>
            <div className="space-y-3 mb-10">
              <div className="border-l-4 border-amber-600 pl-6">
                <p className="text-lg text-slate-900 font-medium">Psychological vulnerability</p>
              </div>
              <div className="border-l-4 border-amber-600 pl-6">
                <p className="text-lg text-slate-900 font-medium">Coercive dynamics</p>
              </div>
              <div className="border-l-4 border-amber-600 pl-6">
                <p className="text-lg text-slate-900 font-medium">Dependency and trust formation</p>
              </div>
              <div className="border-l-4 border-amber-600 pl-6">
                <p className="text-lg text-slate-900 font-medium">Impaired judgment and power asymmetry</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Verification reflects real-world conditions, not controlled environments.
            </p>
          </div>
        </div>
      </section>

      {/* Continuous Verification */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Continuous Verification</p>
            <p className="text-lg text-white font-semibold mb-8">
              Certification is not permanent.
            </p>
            <p className="text-lg text-slate-300 mb-6">Systems are subject to:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Ongoing monitoring</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Randomised testing</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-6">
                <p className="text-lg text-slate-200">Continuous validation</p>
              </div>
            </div>
            <p className="text-lg text-white font-semibold">
              A system that no longer meets the standard loses certification.
            </p>
          </div>
        </div>
      </section>

      {/* Enforcement */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Enforcement</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">SAFE Labs does not provide guidance.</p>
              <p className="text-lg text-slate-700">It does not assist systems to pass.</p>
              <p className="text-lg text-slate-900 font-semibold">It verifies whether they do.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Consequence */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Consequence</p>
            <h2 className="heading-2 mb-8">Without independent verification:</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Safety cannot be proven.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Certification cannot be trusted.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Risk cannot be measured.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Truth */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <div className="space-y-4">
              <p className="text-lg text-slate-300">SAFE cannot exist without SAFE Labs.</p>
              <p className="text-lg text-white font-semibold">Without verification, a standard has no authority.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Line + CTA */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-2">
            If your system is safe —
          </p>
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-12">
            prove it.
          </p>
          <button
            onClick={() => setShowCertForm(true)}
            className="btn-primary"
            data-testid="btn-request-certification"
          >
            Request Certification
          </button>
        </div>
      </section>

      <CertificationInterestForm
        isOpen={showCertForm}
        onClose={() => setShowCertForm(false)}
      />
      <SafeLabsCTA />
    </div>
  );
};

export default LabsPage;
